import { gql } from '@apollo/client/core'
import {
  CreateMessagePostDto,
  MessagePostDto,
  MessagePostGetArgs,
  UpdateMessagePostDto
} from 'booksprout'
import { useApollo } from '../apollo/apolloModule'
import { useBaseCrud } from '../../composables/useBaseCrud'

/**
 * Replaces `MessagePostService`.
 *
 * The class extended `BaseCrudService` for helpers it never used — every method here is a plain
 * apollo query or mutation — so it spreads `useBaseCrud` for the shared surface and nothing else.
 * `apiControllerPath` was already empty: posts have no REST controller.
 */

const messagePostFragments = {
  MessagePost: gql`
    fragment MessagePostFragment on MessagePostDto {
      id,
      createdDate,
      userId,
      state,
      message,
      sender {
        modelKey,
        id,
        name,
        image
      }
    }
  `
}

const crud = useBaseCrud<MessagePostDto>('')

const get = (params?: MessagePostGetArgs): Promise<MessagePostDto[]> => {
  return useApollo().query({
    variables: {
      ...params
    },
    query: gql`
      query GetMessagePosts (
        $threadId: Int!
        $state: Int
        $forUserId: Int
      ) {
        messagePosts (
          threadId: $threadId,
          state: $state,
          forUserId: $forUserId
        ) {
          ...MessagePostFragment
        }
      }
      ${messagePostFragments.MessagePost}
    `
  })
}

const getById = (id?: number): Promise<MessagePostDto> => {
  if (id === void 0) {
    return Promise.reject(new Error('Missing id.'))
  }

  return useApollo().query({
    variables: {
      id
    },
    query: gql`
      query GetMessagePost (
        $id: Int!
      ) {
        messagePost (id: $id) {
          ...MessagePostFragment
        }
      }
      ${messagePostFragments.MessagePost}
    `
  })
}

const create = (model: CreateMessagePostDto): Promise<MessagePostDto> => {
  return useApollo().mutate({
    variables: {
      ...model
    },
    mutation: gql`
      mutation CreateMessagePost (
        $message: String!
        $threadId: Int!
        $penNameId: Int
        $arcTeamId: Int
      ) {
        createMessagePost (
          threadId: $threadId,
          message: $message
          penNameId: $penNameId,
          arcTeamId: $arcTeamId
        ) {
          ...MessagePostFragment
        }
      }
      ${messagePostFragments.MessagePost}
    `
  })
}

const update = (model: UpdateMessagePostDto): Promise<MessagePostDto> => {
  return useApollo().mutate({
    variables: {
      ...model
    },
    mutation: gql`
      mutation UpdateMessagePost (
        $id: Int!
        $state: Int!
      ) {
        updateMessagePost (
          id: $id,
          state: $state
        ) {
          ...MessagePostFragment
        }
      }
      ${messagePostFragments.MessagePost}
    `
  })
}

const deletePost = (id?: number): Promise<any> => {
  return useApollo().mutate({
    variables: {
      id
    },
    mutation: gql`
      mutation DeleteMessagePost (
        $id: Int!
      ) {
        deleteMessagePost(id: $id) {
          id
        }
      }
    `
  })
}

const markAllAsRead = (threadId: number): Promise<number> => {
  return useApollo().mutate({
    variables: {
      id: threadId
    },
    mutation: gql`
      mutation MarkAllPostsAsRead (
        $id: Int!
      ) {
        markAllPostsAsRead(id: $id)
      }
    `
  })
}

const flag = (postId: number): Promise<MessagePostDto> => {
  return useApollo().mutate({
    variables: {
      id: postId
    },
    mutation: gql`
      mutation FlagPost (
        $id: Int!
      ) {
        flagPost(id: $id) {
          ...MessagePostFragment
        }
      }
      ${messagePostFragments.MessagePost}
    `
  })
}

/**
 * `globalLoader` is chained by the list mixins, so it hands back this bag rather than apollo's.
 */
const service = {
  ...crud,

  get,
  getById,
  create,
  update,
  delete: deletePost,
  markAllAsRead,
  flag,

  globalLoader (enable: boolean) {
    crud.setGlobalLoader(enable)
    return service
  }
}

export function messagePostService () {
  return service
}
